import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { getEmissionsSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';

const EmissionsManagementEChart = ({
	title,
	legendPosition = 'bottom',
	defaultFilters = {
		option: 'dia',
	},
	minHeight,
	maxHeight,
	companyId = null,
}) => {
	const dispatch = useDispatch();
	const [selectedDate, setSelectedDate] = useState(defaultFilters.option);

	const selectorEmissions = createSelector(
		state => state.FuelSupply,
		data => ({ info: data.emissionsSummaryManagement || null })
	);
	const { info: infoEmissions } = useSelector(selectorEmissions);

	const fetchEmissionsData = useCallback(
		(filter, company_id) => {
			dispatch(
				getEmissionsSummary({
					// NO establishment_id here - this is for management view
					filter: filter,
					group: 'establecimiento', // Group by establishment
					company_id: company_id, // Add company filter
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		fetchEmissionsData(selectedDate, companyId);
	}, [selectedDate, companyId, fetchEmissionsData]);

	const { series, categories, legendData } = useMemo(() => {
		if (!infoEmissions?.data || !Array.isArray(infoEmissions.data))
			return { series: [], categories: [], legendData: [] };

		// Extract unique dates
		const dates = [...new Set(infoEmissions.data.map(item => item.date))];

		// Extract unique establishments
		const establishmentsMap = new Map();
		infoEmissions.data.forEach(item => {
			if (item.establishments && Array.isArray(item.establishments)) {
				item.establishments.forEach(est => {
					if (!establishmentsMap.has(est.establishment_id)) {
						establishmentsMap.set(
							est.establishment_id,
							est.establishment_name
						);
					}
				});
			}
		});

		const establishments = Array.from(establishmentsMap.entries());

		// Create series for each establishment
		const seriesData = establishments.map(([estId, estName]) => ({
			name: estName,
			type: 'bar',
			stack: 'total',
			data: dates.map(date => {
				const dateData = infoEmissions.data.find(
					item => item.date === date
				);
				if (!dateData || !dateData.establishments) return 0;

				const estData = dateData.establishments.find(
					e => e.establishment_id === estId
				);
				return estData ? Number(estData.total_emissions) || 0 : 0;
			}),
		}));

		return {
			series: seriesData,
			categories: dates,
			legendData: establishments.map(([_, name]) => name),
		};
	}, [infoEmissions?.data]);

	const options = useMemo(
		() => ({
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'shadow' },
				formatter: function (params) {
					if (!params || !params.length) return '';
					let result = params[0].axisValue + '<br/>';
					let total = 0;

					params.forEach(param => {
						if (param.value > 0) {
							result +=
								param.marker +
								' ' +
								param.seriesName +
								': ' +
								param.value.toFixed(2) +
								' kgCO2<br/>';
							total += param.value;
						}
					});

					result += '<b>Total: ' + total.toFixed(2) + ' kgCO2</b>';
					return result;
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			xAxis: [{ type: 'category', data: categories }],
			yAxis: [{ type: 'value', name: 'kgCO2' }],
			series: series,
		}),
		[series, categories, legendData]
	);

	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const [hasValidData, setHasValidData] = useState(false);

	useEffect(() => {
		if (Array.isArray(infoEmissions?.data)) {
			setHasLoadedOnce(true);
			if (infoEmissions.data.length > 0) {
				setHasValidData(true);
			}
		}
	}, [infoEmissions?.data]);

	// Only show empty state if we've loaded and confirmed there's no data
	const noData = hasLoadedOnce && !infoEmissions?.data?.length;

	const actions = (
		<div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
			<Select
				placeholder='Fecha'
				data={[
					{ label: 'Ciclo', value: 'ciclo' },
					{ label: 'Año', value: 'año' },
					{ label: 'Semestre', value: 'semestre' },
					{ label: 'Mes', value: 'mes' },
					{ label: 'Día', value: 'dia' },
				]}
				value={selectedDate}
				onChange={value => value && setSelectedDate(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
		</div>
	);

	return (
		<OChartCard
			title={title}
			actions={actions}
			isLoading={!hasLoadedOnce && !infoEmissions?.data}
			error={null}
			isEmpty={noData}
			emptyIconClass='ri-leaf-line'
			emptyMessage='No cuenta con registro de emisiones de combustible'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default React.memo(EmissionsManagementEChart);
